// Full Badges & Trophies page — categories, progress, claim flow
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AccentRule, SectionTitle } from './studentUi';
import { Button } from '@/components/ui/button';
import api from './studentApi';
import { Trophy, Lock, Award, Package } from 'lucide-react';
import TrophyClaimModal from './TrophyClaimModal';
import { BadgesSkeleton } from './StudentSkeletons';

const FAMILY_LABELS = {
  streak:     { label: 'Daily Streaks',  icon: '🔥', desc: 'Build a habit, earn a flame' },
  curiosity:  { label: 'Curiosity',      icon: '❓', desc: 'Reward for asking questions' },
  resolution: { label: 'Resolution',     icon: '💡', desc: 'Solve doubts, gain insight' },
  mileage:    { label: 'Mileage',        icon: '🛤️', desc: 'Distance traveled in learning' },
  mastery:    { label: 'Mastery',        icon: '🎓', desc: 'Lessons completed & focused time' },
  special:    { label: 'Special',        icon: '✨', desc: 'Hidden achievements you stumble upon' },
};

const FAMILIES_ORDER = ['streak', 'curiosity', 'resolution', 'mileage', 'mastery', 'special'];

export default function StudentBadgesPage() {
  const [tab, setTab] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [claimOpen, setClaimOpen] = useState(null);

  const { data: page, refetch: load } = useQuery({
    queryKey: ['student', 'badges'],
    queryFn: async () => {
      const [list, trophies] = await Promise.all([
        api.get('/student/badges/list'),
        api.get('/student/badges/trophies').catch(() => null),
      ]);
      return { list, trophies };
    },
  });
  const data = page?.list ?? null;
  const trophies = page?.trophies ?? null;

  const badges = data?.badges || [];
  const filtered = useMemo(() => {
    let arr = badges;
    if (tab === 'earned') arr = arr.filter(b => b.earned);
    if (tab === 'locked') arr = arr.filter(b => !b.earned);
    if (familyFilter !== 'all') arr = arr.filter(b => b.family === familyFilter);
    return arr;
  }, [badges, tab, familyFilter]);

  if (!data) return <BadgesSkeleton />;

  const earnedCount = data.earned_count || 0;

  return (
    <div className="space-y-10 max-w-6xl pb-20" data-testid="student-badges-page">
      <header>
        <h1 className="text-3xl font-semibold leading-tight" style={{ color: 'var(--st-ink)' }}>Badges & Trophies</h1>
<AccentRule className="mt-2" />
        <p className="text-sm opacity-70 mt-1">Earn badges by being active, asking questions and clearing doubts. Hit milestones to unlock physical Deskey trophies, posted to your home</p>
      </header>

      {/* Trophy Showcase */}
      <section data-testid="trophy-showcase">
        <SectionTitle title="Deskey trophies" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(trophies?.trophies || []).map(t => {
            const status = t.order?.status;
            const pct = Math.min(100, Math.round((t.progress / t.threshold) * 100));
            return (
              <div key={t.id} className="relative rounded-3xl p-5 border-2 overflow-hidden flex flex-col"
                data-testid={`trophy-card-${t.id}`}
                style={{
                  borderColor: t.unlocked ? t.color : 'var(--st-border)',
                  background: t.unlocked ? `linear-gradient(135deg, ${t.color}22 0%, var(--st-card) 70%)` : 'var(--st-card)',
                }}>
                {!t.unlocked && <Lock className="absolute top-3 right-3 w-4 h-4 opacity-40" />}
                <div className="text-5xl mb-2" style={{ filter: t.unlocked ? 'none' : 'grayscale(0.8)' }}>{t.icon}</div>
                <div className="text-base font-semibold">{t.name}</div>
                <div className="text-xs opacity-70 mt-1 flex-1">{t.desc}</div>

                {!t.unlocked ? (
                  <>
                    <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-border)' }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: t.color }} />
                    </div>
                    <div className="text-xs opacity-60 mt-1">{t.progress} / {t.threshold} badges</div>
                  </>
                ) : status === 'unlocked' ? (
                  <Button onClick={() => setClaimOpen(t)} data-testid={`trophy-claim-${t.id}`}
                    className="mt-3 rounded-full text-white text-xs" style={{ background: t.color, color: '#000' }}>
                    Claim — ship it to me!
                  </Button>
                ) : (
                  <div className="mt-3 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                    style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
                    <Package className="w-3 h-3" />
                    <span className="capitalize">{status}</span>
                    {t.order?.tracking_number && <span className="opacity-60 font-mono ml-1">{t.order.tracking_number}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="badge-stats">
        <StatCard label="Earned"      value={earnedCount} icon={<Award className="w-4 h-4" />} />
        <StatCard label="Total"       value={data.total} />
        <ProgressCard earned={earnedCount} />
      </section>

      {/* Filter tabs */}
      <section data-testid="badge-list-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="inline-flex p-1 rounded-2xl border-2" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
            {[
              { id: 'all',    label: 'All'    },
              { id: 'earned', label: 'Earned' },
              { id: 'locked', label: 'Locked' },
            ].map(t => {
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`badge-tab-${t.id}`}
                  className="px-4 py-1.5 rounded-xl text-xs font-semibold transition"
                  style={{ background: active ? 'var(--st-primary)' : 'transparent', color: active ? 'var(--st-on-primary)' : 'inherit' }}>
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="inline-flex flex-wrap gap-1.5">
            <button onClick={() => setFamilyFilter('all')} data-testid="badge-family-all"
              className="px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition"
              style={{
                borderColor: familyFilter === 'all' ? 'var(--st-primary)' : 'var(--st-border)',
                background: familyFilter === 'all' ? 'var(--st-primary)' : 'var(--st-card)',
                color: familyFilter === 'all' ? '#fff' : 'inherit',
              }}>
              All categories
            </button>
            {FAMILIES_ORDER.map(f => {
              const meta = FAMILY_LABELS[f];
              const active = familyFilter === f;
              return (
                <button key={f} onClick={() => setFamilyFilter(f)} data-testid={`badge-family-${f}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition"
                  style={{
                    borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                    background: active ? 'var(--st-primary)' : 'var(--st-card)',
                    color: active ? 'var(--st-on-primary)' : 'inherit',
                  }}>
                  {meta.icon} {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl p-8 border-2 text-center text-sm opacity-60" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
            No badges match this filter.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map(b => <BadgeCard key={b.id} badge={b} />)}
          </div>
        )}
      </section>

      {claimOpen && (
        <TrophyClaimModal trophy={claimOpen} onClose={() => setClaimOpen(null)} onClaimed={() => { setClaimOpen(null); load(); }} />
      )}
    </div>
  );
}

function BadgeCard({ badge }) {
  const earned = badge.earned;
  const tierGlow = ['', '0 0 0 2px var(--st-primary)25', '0 0 0 3px var(--st-primary)40', '0 4px 12px var(--st-primary)30', '0 6px 16px var(--st-accent)50', '0 8px 24px var(--st-accent)70'];
  return (
    <div className="rounded-2xl p-4 border-2 transition relative overflow-hidden flex flex-col"
      data-testid={`badge-card-${badge.id}`}
      style={{
        borderColor: earned ? 'var(--st-primary)' : 'var(--st-border)',
        background: 'var(--st-card)',
        boxShadow: earned ? tierGlow[badge.tier] : 'none',
      }}>
      {!earned && <Lock className="absolute top-3 right-3 w-3.5 h-3.5 opacity-40" />}
      <div className="text-4xl mb-2" style={{ filter: earned ? 'none' : 'grayscale(1) opacity(0.6)' }}>{badge.icon}</div>
      <div className="text-sm font-semibold leading-tight">{badge.name}</div>
      <div className="text-xs opacity-60 mt-1 flex-1 line-clamp-2">{badge.desc}</div>
      {earned ? (
        <div className="text-xs mt-2 font-semibold" style={{ color: 'var(--st-primary)' }}>
          ✓ Earned {badge.earned_at ? timeAgo(badge.earned_at) : ''}
        </div>
      ) : badge.metric === '_special' ? (
        <div className="text-xs opacity-60 mt-2 italic">Hidden — keep exploring</div>
      ) : (
        <>
          <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-border)' }}>
            <div className="h-full rounded-full" style={{ width: `${badge.progress_pct}%`, background: 'var(--st-primary)' }} />
          </div>
          <div className="text-xs opacity-60 mt-1">{badge.progress} / {badge.threshold}</div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      <div className="text-sm flex items-center gap-1.5" style={{ color: 'var(--st-mute)' }}>{icon}{label}</div>
      <div className="text-3xl font-semibold tabular-nums mt-1" style={{ color: 'var(--st-primary-ink)', fontFamily: 'var(--st-font-heading)' }}>{value}</div>
    </div>
  );
}

function ProgressCard({ earned }) {
  // Show progress to next 100 milestone
  const NEXT = 100 * Math.ceil((earned + 1) / 100);
  const pct = Math.min(100, Math.round((earned / NEXT) * 100));
  return (
    <div className="rounded-2xl p-4 border-2" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      <div className="text-xs opacity-60">To next milestone</div>
      <div className="text-base font-semibold mt-1">{earned} / {NEXT}</div>
      <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-border)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--st-accent)' }} />
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short' });
}
